/*
  What is this used for?
  This is used to handle the submission of the form in prod-wgac-waitlist.liquid
*/

const ALERT_FORMAT = 'alert-format';
const BLOCK = 'block';
const NONE = 'none';
const ERROR_TEXT = 'Something went wrong. Please try again.';

class WaitlistForm {
  constructor() {
    const { waitlistId, klaviyoAccountId, selectedVariantId } = window.wgacWaitlist || {};
    this.config = { 
      waitlistId, 
      klaviyoAccountId, 
      selectedVariantId,
      // Configuration for posting the back in stock subscription event
      subscriptionType: 'back-in-stock-subscription',
      channels: ['EMAIL'],
      klaviyoApiUrl: `https://a.klaviyo.com/client/back-in-stock-subscriptions?company_id=${klaviyoAccountId}`
    };
    
    this.elements = {
      form: document.getElementById('email_signup_waitlist'),
      formContainer: document.querySelector('.prod-wgac-waitlist-main'),
      successContainer: document.querySelector('.prod-wgac-waitlist-success'),
      errorMessage: document.querySelector('.prod-wgac-waitlist__error_message'),
      waitlistModal: document.querySelector('.custom-waitlist-modal'),
      inputField: document.querySelector('.prod-wgac-waitlist__input'),
      closeButton: document.querySelector('.prod-wgac-waitlist__actions-close')
    };
    
    this.handleBackInStockEventSubmit = this.handleBackInStockEventSubmit.bind(this);
    this.handleCloseButton = this.handleCloseButton.bind(this);
  }
  
  /**
   * We use these next 4 helper functions + handleBackInStockEventSubmit() when we're sending a "Subscribed to Back in Stock" event,
   * i.e. when a list ID is not included in a metaobject; these drive the default functionality.
  */
  validateEmail(email) {
    if (!email || !email.includes('@')) {
      this.elements.errorMessage.style.display = BLOCK;
      if (this.elements.inputField) {
        this.elements.inputField.classList.add(ALERT_FORMAT);
      }
      return false;
    }
    
    this.elements.errorMessage.style.display = NONE;
    if (this.elements.inputField) {
      this.elements.inputField.classList.remove(ALERT_FORMAT);
    }
    return true;
  }
  
  showSuccess() {
    this.elements.formContainer.style.display = NONE;
    this.elements.successContainer.style.display = BLOCK;
  }
  
  showError(error) {
    console.error('Error:', error);
    this.elements.errorMessage.textContent = ERROR_TEXT;
    this.elements.errorMessage.style.display = BLOCK;
    if (this.elements.inputField) {
      this.elements.inputField.classList.add(ALERT_FORMAT);
    }
  }
  
  handleCloseButton() {
    if (this.elements.waitlistModal) {
      this.elements.waitlistModal.style.display = NONE;
    }
    this.elements.successContainer.style.display = NONE;
    this.elements.form.reset();
  }
  
  async handleBackInStockEventSubmit(event) {
    event.preventDefault();
    const email = this.elements.form.querySelector('#subscription_email').value;
    
    if (!this.validateEmail(email)) return;
    
    const options = {
      method: 'POST',
      headers: {
        accept: 'application/vnd.api+json',
        revision: '2025-01-15',
        'content-type': 'application/vnd.api+json'
      },
      body: JSON.stringify({
        data: {
          type: this.config.subscriptionType,
          attributes: {
            profile: {
              data: {
                type: 'profile',
                attributes: {
                  email: email
                }
              }
            },
            channels: this.config.channels
          },
          relationships: {
            variant: {
              data: {
                type: 'catalog-variant',
                id: `$shopify:::$default:::${this.config.selectedVariantId}`
              }
            }
          }
        }
      })
    };
    
    try {
      const response = await fetch(
        this.config.klaviyoApiUrl,
        options
      );
      
      if (!response.ok) {
        throw new Error('Subscription failed');
      }
      
      this.showSuccess();
    } catch (error) {
      console.error('Waitlist submission error:', error);
      this.showError(error);
    }
  }
  
  /**
   * We use attachToKlaviyoForms() and setupKlaviyoFormObserver() when we're signing someone up for a list,
   * i.e. when a list ID is included in a metaobject. Klaviyo's "klaviyo_subscribe.js" script drives this functionality,
   * and these scripts help to initilize that and watch for changes as it updates the page.
   * TO-DO: We may want to stop using this (here and in the footer) and instead start handling the request ourselves
  */
  attachToKlaviyoForms() {
    if (window?.KlaviyoSubscribe) {
      window.KlaviyoSubscribe.attachToForms('#email_signup_waitlist', {
        hide_form_on_success: true,
        extra_properties: {
          $source: 'Ecomm_Footer'
        }
      });
    }
  }

  /**
   * This watches for klaviyo_subscribe to update success and error messages, and then hides those
   * so we can handle them ourselves.
   */
  setupKlaviyoFormObserver() {
    const popupContent = document.querySelector('.custom-waitlist-modal-content');
    if (!popupContent) return;

    const config = { childList: true, subtree: true };

    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          const successMessage = popupContent.querySelector('.success_message');
          const errorMessage = popupContent.querySelector('.error_message');
          const main = popupContent.querySelector('.prod-wgac-waitlist-main');
          const waitlistSuccess = popupContent.querySelector('.prod-wgac-waitlist-success');
          const waitlistErrorMessage = popupContent.querySelector('.prod-wgac-waitlist__error_message');

          // Handle error message display
          if (errorMessage && errorMessage.style.display !== NONE) {
            errorMessage.style.display = NONE;
            if (waitlistSuccess) waitlistSuccess.style.display = NONE;
            if (waitlistErrorMessage) waitlistErrorMessage.style.display = BLOCK;
            if (this.elements.inputField) this.elements.inputField.classList.add(ALERT_FORMAT);
            return;
          }

          // Handle success message display
          if (successMessage && successMessage.style.display !== NONE && main) {
            successMessage.style.display = NONE;
            if (waitlistErrorMessage) waitlistErrorMessage.style.display = NONE;
            if (waitlistSuccess) {
              main.style.display = NONE;
              waitlistSuccess.style.display = BLOCK;
            }
            if (this.elements.inputField) this.elements.inputField.classList.remove(ALERT_FORMAT);
          }
        }
      }
    });

    // Begin observing the content of the popup
    observer.observe(popupContent, config);

    // Add close button for Klaviyo list form
    const modalCloseButton = popupContent.querySelector('.prod-wgac-waitlist__actions-close');
    if (modalCloseButton) {
      modalCloseButton.addEventListener('click', () => {
        if (this.elements.waitlistModal) {
          this.elements.waitlistModal.style.display = NONE;
        }
      });
    }
  }
  
  init() {
    // Check if form exists
    if (!this.elements.form) {
      console.error('Waitlist form not found');
      return;
    }
    
    // Attach close button handler
    if (this.elements.closeButton) {
      this.elements.closeButton.addEventListener('click', this.handleCloseButton);
    }
    
    // Determine which mode to use based on configuration
    if (this.config.waitlistId) {
      // If waitlist_id exists, use Klaviyo form attachment to sign up to list
      this.attachToKlaviyoForms();
      this.setupKlaviyoFormObserver();
    } else {
      // Otherwise use back in stock event
      this.elements.form.addEventListener('submit', this.handleBackInStockEventSubmit);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const waitlistForm = new WaitlistForm();
  waitlistForm.init();
});
!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{},n=(new e.Error).stack;n&&(e._sentryDebugIds=e._sentryDebugIds||{},e._sentryDebugIds[n]="50c260fc-6376-56f7-9bfd-3c76ffa0b25a")}catch(e){}}();
//# debugId=50c260fc-6376-56f7-9bfd-3c76ffa0b25a
